<template>
    <div class="glossary-wrapper full-size"
         @touchstart.prevent="removeGlossary"
         @click.prevent="removeGlossary"
         :style="data.styleGlossaryWrapper"
    >
        <div class="glossary"
             :style="data.styleGlossary"
        >
            <iframe scrolling= "no"
                    frameBorder="0"
                    :src="bookInfoState.activeGlossaryData.path"
                    @load="iframeOnLoaded"
                    class="full-size"
            >
            </iframe>
            <div class="arrow"
                 :class="data.classArrow"
                 :style="data.styleArrow"
                 v-show="data.iFrameLoaded"
            ></div>
        </div>
    </div>
</template>

<script>
    import {ref, reactive, onMounted, onUpdated, watchEffect, watch, computed, nextTick}  from "vue";
    import {useParser} from "@/modules/reader/annotations/parser";
    import {useBookInfo} from "@/modules/reader/book/bookinfo";
    import {usePageResize} from "@/modules/reader/book/pageResize";
    import {usePaging} from "@/modules/reader/book/paging";
    import {useAnnotationHelper} from "@/modules/reader/annotations/annotationHelper";
    import config from "@/config/config.json";
    import cssvar from '@/scss/_variables.scss';
    import axios from 'axios'

    const {getResizedAnnotationPos} = useAnnotationHelper();
    const zIndexGlossary = parseInt(cssvar.zIndexGlossary);

    export default {
        setup(props, context) {

            const { bookInfoState, removeGlossary} = useBookInfo();
            const { pageResizeState} = usePageResize();
            const { pageState } = usePaging();

            const headerHeight = parseInt(cssvar.headerHeight)
            const btnPos = bookInfoState.activeGlossaryData.btnPos;
            const btnBoundingRect=  bookInfoState.activeGlossaryData.btnBoundingRect;

            //the arrow is created from a square, then rotated using CSS; arrowSize defines the square side length in pixel:
            const arrowSize = 16;
            //After rotation, the arrow width is actually the diagonal of the square, calculated by the Pythagorean theorem:
            const arrowWidth = Math.round(Math.sqrt(2) * arrowSize);
            //The arrow is formed by a triangle, which takes half of the diagonal:
            const arrowHeight = arrowWidth * 0.5;
            //After the square is rotated, there are some space occupied since the diagonal is larger than the square length. Get this space size to position the arrow accurately:
            const arrowRotatedAngleSize  = arrowHeight - arrowSize * 0.5;
            const arrowCloseToEdgeSpace = 20; //set the margin between arrow and the edge, if arrow is not positioned to center of glossary horizontally

            const data = reactive({
                iFrameLoaded:false,
                width:0,
                height:0,
                glossaryFrameLeftIfCentered:0,//retrieve the left position if the glossary is expected to place at the center of the page, this happens if the glossary is out of boundary
                pageWidth:0, //the width of the main content image, expected to include zoomed level and image-to-browser scale
                zoomLevel:1,
                forceScale:1, //the glossary won't be scaled normally, but if it's too large, force to scale it
                overBtnAreaHeight:0,
                belowBtnAreaHeight:0,
            });

            let boxEl, explainDiv;

            const iframeOnLoaded = async (e)=> {
                let iframeEl = e.target;
                let iframeDoc = iframeEl.contentDocument || iframeEl.contentWindow.document;
                //the size can be determined from the '.box' selector inside the target html

                //nativeAccess.js in glossary will call app, which will cause 'the browser repeatedly opens another application' in tablet browser.
                //setting nativeAccess and onSuccess callback to null will resolve this issue
                iframeEl.contentWindow.nativeAccess = null;
                iframeEl.contentWindow.onSuccess = null;

                //the glossary main image file name is normally the html query param...ß
                let glossaryid = bookInfoState.activeGlossaryData.path.split("html?id=")[1];
                let glossaryImagePath;
                boxEl = iframeDoc.body.querySelector('.box');
                let boxSize = boxEl.getBoundingClientRect();
                let explainImgEl = boxEl.querySelector(".explainImg");

                //the image element and src should be able to retrieve normally. It may not be able to get in tablet.
                if (explainImgEl && (explainImgEl.src.indexOf(".jpg") >= 0 || explainImgEl.src.indexOf(".png") >=0  || explainImgEl.src.indexOf(".gif") >= 0 )) {
                    glossaryImagePath = explainImgEl.src;
                //if image element cannot be retrieved, try reading jpg/png/gif using the glossary id as filename
                } else {
                    let glossaryIdFullPath = `${bookInfoState.ebookDownloadPath}/dictionary/image/${glossaryid}`;
                    let testPngPath = `${glossaryIdFullPath}.png`;
                    let testJpgPath = `${glossaryIdFullPath}.jpg`;
                    let testGifPath = `${glossaryIdFullPath}.gif`;

                    let testPngPromise = await axios.get(testPngPath).catch(() => null);
                    let testJpgPromise = await axios.get(testJpgPath).catch(() => null);
                    let testGifPromise = await axios.get(testGifPath).catch(() => null);

                    if (testPngPromise) {
                        glossaryImagePath = testPngPath;
                    } else if (testJpgPromise) {
                        glossaryImagePath = testJpgPath;
                    } else if (testGifPromise) {
                        glossaryImagePath = testGifPath;
                    }
                }

                //if glossary image path is not detected, the glossary content should be text, which is loaded from xml (see the iframe codes for details...)
                //In this case, get the content size after the xml is loaded.
                if (!glossaryImagePath) {
                    //data.width = Math.round(boxSize.width);
                    //data.height = Math.round(boxSize.height)
                    await axios.get(`${bookInfoState.ebookDownloadPath}/dictionary/data/data.xml`);
                    //setTimeout(()=> {
                        explainDiv =  iframeDoc.body.querySelector('#g_explanation');
                        let explainDivSize = explainDiv.getBoundingClientRect();
                        data.width = Math.round(explainDivSize.width +  config.reader.glossary.padding * 2 );
                        data.height = Math.round(explainDivSize.height +  config.reader.glossary.padding * 2);
                    //}, 200)
                } else {

                    //read the glossary physical dimension, this will make sure the glossary box size is accurate to contain the full image
                    let img = new Image();
                    const imageLoadPromise = new Promise(resolve => {
                        img = new Image();
                        img.onload = resolve;
                        img.src = glossaryImagePath;
                    });

                    await imageLoadPromise;

                    //Normally the image path should be able to retrieve
                    if (glossaryImagePath) {
                        //it's observed that the glossary width (i.e. the glossary image set in the glossary) is always fixedWidth (405px)
                        let scaleRatio = config.reader.glossary.fixedWidth / img.width;
                        let imgScaledHeight = Math.round(img.height * scaleRatio);
                        data.width = config.reader.glossary.padding * 2 + config.reader.glossary.fixedWidth;
                        data.height = config.reader.glossary.padding * 2 + imgScaledHeight;

                    //If in rare cases, the image path is not found, just use the boxSize... This should never occur if the glossary follows the authoring rules...
                    } else {
                        data.width = Math.round(boxSize.width);
                        data.height = Math.round(boxSize.height);
                    }
                }

                data.overBtnAreaHeight = btnBoundingRect.top - headerHeight;
                data.belowBtnAreaHeight = document.querySelector(".glossary-wrapper").clientHeight - btnBoundingRect.bottom + headerHeight;
                checkForceScale();
                data.iFrameLoaded = true;
            }

            const checkForceScale = ()=>{

                if (data.height >  data.overBtnAreaHeight && data.height > data.belowBtnAreaHeight) {
                    data.forceScale = data.overBtnAreaHeight / data.height * config.reader.glossary.forceScale;
                }

                //this happens if the browser window is small enough (e.g. on tablet or if browser is scaled down)
                if (data.width > data.pageWidth) {
                    let updatedWidth  =  data.pageWidth * config.reader.glossary.forceScale;
                    let scale = data.width / updatedWidth;
                    data.width = updatedWidth;
                    if (!explainDiv) {
                        data.height = data.height / scale;
                    }
                    boxEl.style.width = "auto";
                }

            }

            watchEffect(()=>{

                if (!bookInfoState.activeGlossaryData) {
                    return;
                }

                let arrowDirection;

                if ((pageResizeState.zoomLevel || pageResizeState.zoomLevelLeft || pageResizeState.zoomLevelRight) && data.iFrameLoaded) {

                    data.overBtnAreaHeight = btnBoundingRect.top - headerHeight;
                    data.belowBtnAreaHeight = document.querySelector(".glossary-wrapper").clientHeight  - btnBoundingRect.bottom + headerHeight;
                    checkForceScale();

                }
                if (pageState.pageMode === "single") {
                    data.zoomLevel =  pageResizeState.zoomLevel;
                    data.pageWidth = pageResizeState.fittedBrowserWidth;
                } else {
                    if (bookInfoState.activeGlossaryData.pagenum  % 2 === 0) {
                        data.zoomLevel = pageResizeState.zoomLevelLeft;
                        data.pageWidth = pageResizeState.fittedBrowserZoomedLeftWidth;
                    } else {
                        data.zoomLevel = pageResizeState.zoomLevelRight;
                        data.pageWidth = pageResizeState.fittedBrowserZoomedRightWidth;
                    }
                }

                data.glossaryFrameLeftIfCentered = (pageResizeState.fittedBrowserWidth * data.zoomLevel - data.width * data.zoomLevel) * 0.5;

                data.leftPos = computed(() => {

                    //btnLeftMargin is the space between the image left edge and the btn left
                    let btnLeftMargin = btnPos.left * data.zoomLevel;
                    //btnRightMargin is the space between the image right edge and the btn right
                    let btnRightMargin = pageResizeState.baseWidth * data.zoomLevel - btnLeftMargin + btnPos.width
                    let glossaryHalfWidth = data.width * 0.5;
                    let glossaryFrameLeftPos;
                    //if moved to center, the arrow should be shifted also.
                    let arrowOffsetLeft = 0;

                    console.log("btnLeftMargin: " + btnLeftMargin);
                    console.log("btnRightMargin: " + btnRightMargin);
                    console.log("glossaryHalfWidth: " + glossaryHalfWidth);
                    console.log("page width: " + data.pageWidth);
                    console.log("glossary width: " + data.width)

                    //if left margin is larger the right one, place the glossary on the right hand side of btn
                    if (btnLeftMargin > btnRightMargin) {
                        glossaryFrameLeftPos = Math.round(btnPos.left - data.width / data.zoomLevel + btnPos.width + arrowCloseToEdgeSpace / data.zoomLevel);
                        //if out of boundary (extended to the left edge of the page), move to center
                        //Out of boundary should occur when zoom level is one only, since the glossary width set in the iframe is fixed (490px),
                        //when zoom level is enlarged, the page should be wide enough to hold the glossary.
                        if (glossaryFrameLeftPos < 0 && data.zoomLevel === 1) {
                            arrowOffsetLeft = -1 * glossaryFrameLeftPos + (data.pageWidth - data.width) * 0.5;
                            glossaryFrameLeftPos = data.glossaryFrameLeftIfCentered;

                        }

                        return {
                            glossaryFrame: glossaryFrameLeftPos,
                            arrow: Math.round(data.width - arrowCloseToEdgeSpace - btnPos.width * data.zoomLevel * 0.5 - arrowWidth * 0.5 - arrowOffsetLeft)
                        }

                    //if right margin is larger the left one, place the glossary on the right hand side of btn
                    } else if (btnRightMargin > btnLeftMargin) {
                        glossaryFrameLeftPos = Math.round(btnPos.left - arrowCloseToEdgeSpace / data.zoomLevel);
                        //if out of boundary (extended to the right edge of the page), move to center.
                        //Out of boundary should occur when zoom level is one only, since the glossary width set in the iframe is fixed (490px),
                        //when zoom level is enlarged, the page should be wide enough to hold the glossary.
                        if (glossaryFrameLeftPos + data.width * data.zoomLevel > data.pageWidth && data.zoomLevel === 1) {
                            arrowOffsetLeft = glossaryFrameLeftPos - data.glossaryFrameLeftIfCentered;
                            glossaryFrameLeftPos = data.glossaryFrameLeftIfCentered;
                            console.log("right edge out bound!!! " + glossaryFrameLeftPos)
                        }

                        return {
                            glossaryFrame: glossaryFrameLeftPos,
                            arrow: Math.round(arrowCloseToEdgeSpace + btnPos.width * data.zoomLevel * 0.5 - arrowWidth * 0.5 + +arrowOffsetLeft)
                        }

                        //if both margins should be large enough hold half width of glossary
                    } else if (btnLeftMargin > glossaryHalfWidth * data.zoomLevel && btnRightMargin > glossaryHalfWidth * data.zoomLevel) {
                        return {
                            glossaryFrame: Math.round(btnPos.left + 0.5 * btnPos.width - glossaryHalfWidth / data.zoomLevel),
                            arrow: Math.round(glossaryHalfWidth - 0.5 * arrowWidth / data.zoomLevel)
                        }
                    }
                });

                data.topPos = computed(() => {

                    //if enough space over btn
                    if (data.overBtnAreaHeight  >= data.height || data.forceScale < 1) {

                        let heightDiff =  data.height / data.zoomLevel - data.height / data.zoomLevel * data.forceScale
                        console.log("height diff after force resize: " + heightDiff);

                        arrowDirection = "down";
                        return {
                            glossaryFrame: Math.round(btnPos.top - data.height / data.zoomLevel) - (arrowHeight - arrowRotatedAngleSize) / data.zoomLevel + heightDiff,
                            arrow: arrowRotatedAngleSize  + data.height - arrowHeight + 1 //one of the side of the square is missing border, so add one more pixel
                        }

                        //if enough space below btn
                    } else {

                        arrowDirection = "up";
                        return {
                            glossaryFrame: Math.round( (btnPos.top + btnPos.height) + (arrowHeight - arrowRotatedAngleSize) / data.zoomLevel),
                            arrow: arrowRotatedAngleSize - arrowHeight + 1
                        }
                    }

                });

                data.styleGlossary = computed(() => {

                    return {
                        width: `${data.width}px`,
                        height: `${data.height}px`,
                        transform: `scale(${1 / data.zoomLevel * data.forceScale}`, //not that transform origin is left top
                        top: `${data.topPos.glossaryFrame}px`,
                        left: `${data.leftPos.glossaryFrame}px`,
                        //transformOrigin:(data.forceScale === 1) ? "left top" : "left bottom"
                        transformOrigin: "left top",
                        //zIndex: zIndexGlossary + document.querySelectorAll(".hint").length //tricky: this makes sure glossary must be on top of hint layer!
                    }

                });

                data.styleGlossaryWrapper = computed(() => {

                    return {
                        zIndex: zIndexGlossary + document.querySelectorAll(".hint").length //tricky: this makes sure glossary must be on top of hint layer!
                    }

                });

                data.styleArrow = computed(() => {
                    return {
                        width: `${arrowSize}px`,
                        height: `${arrowSize}px`,
                        top: `${data.topPos.arrow}px`,
                        left: `${data.leftPos.arrow}px`
                    }
                });

                data.classArrow = computed(() => {
                    return {
                        "up": (arrowDirection === "up")
                    }
                })

            })

            return {
                props,
                data,
                iframeOnLoaded,
                pageResizeState,
                bookInfoState,
                removeGlossary,
                getResizedAnnotationPos
            }


        }
    }

</script>


<style scoped lang="scss">

    iframe {
        border:none;
        width:100%;
        height:100%;
        position:absolute;
        border:1px solid #008b8b;
        overflow: hidden;
        border-radius: 6px;
    }

    .glossary-wrapper {
        position:fixed;
        left:0;
        top:0;
        /*background:rgba(0,0,0,.4);*/
        //z-index:$zindex-glossary;
    }

    .glossary {
        //z-index:$zindex-glossary;
        position: absolute;
        box-shadow: 2px 2px 6px #999;
        transform-origin: left top;
        //opacity:0.7;*

    }

    .mask {
        position:fixed;
        left:0;
        top:0;
        background:rgba(0,0,0,.4);
        z-index:$zindex-glossary;;
    }

    .arrow {
        background:#fff;
        border: solid #008b8b;
        border-width: 0 1px 1px 0;
        position:absolute;
        box-sizing:border-box;
        transform: rotate(45deg);
        -webkit-transform: rotate(45deg);

        &.up {
            transform: rotate(-135deg) !important;
            -webkit-transform: rotate(-135deg) !important;
        }
    }
</style>
